import { useRef, useState, type ReactNode } from 'react';
import { Badge } from './Badge.js';
import { Icon } from './Icon.js';
import { IconButton } from './IconButton.js';
import { VisuallyHidden } from './VisuallyHidden.js';
import type { RailItem } from './Rail.js';
import './Sidebar.css';

export interface SidebarSubItem {
  id: string;
  label: string;
  href?: string;
  onSelect?: () => void;
}

export interface SidebarItem extends RailItem {
  /**
   * Sub-navigation, revealed inline under the item when it is the active one.
   * The prototype expands the active group in place rather than in a flyout —
   * a flyout hides the child routes until hover, and these are destinations a
   * user navigates between repeatedly (Findings > Ranked feed / Finding detail /
   * Audit reports).
   */
  children?: SidebarSubItem[];
}

export interface SidebarProps {
  items: SidebarItem[];
  activeId?: string;
  /** Active sub-item id, when the active item has children. */
  activeSubId?: string;
  /**
   * Top block — the org switcher in the prototype. Occupies the same height as
   * the topbar beside it so the two hairlines align into one bar.
   */
  header?: ReactNode;
  /**
   * The always-present contextual-help entry point, identical in contract to
   * `Rail`'s (FR-25.6) — a Sidebar cannot be constructed without one. Pinned to
   * the bottom, never scrolled away with the nav.
   */
  /** Rendered beside the help button — e.g. a secondary action. */
  pinnedExtra?: ReactNode;
  /**
   * Collapses the sidebar to the icon rail. Rendered as a hamburger at the
   * top-LEFT of the header, BEFORE the org switcher — the prototype's own order,
   * and what makes it read as "collapse this panel" rather than a topbar action.
   */
  onCollapse?: () => void;
}

/**
 * The expanded, labelled primary navigation (design-system catalogue, Tier 2).
 *
 * This is the **desktop default**; `Rail` is its collapsed state, not the other
 * way round. The design-fidelity audit found the build shipping only the rail at
 * every desktop width, which is the shape the prototype shows *after* the user
 * collapses the nav (audit S-1).
 *
 * The accessible-name contract is deliberately identical to `Rail`'s so the two
 * announce the same thing: the visible count badge is `aria-hidden` and the count
 * is folded into the item's name using the caller's own `badgeLabel` wording,
 * because 08 §2 gives each nav badge a different source and labelling them all
 * "need attention" is how badges stop being read.
 */
export function Sidebar({
  items,
  activeId,
  activeSubId,
  header,
  pinnedExtra,
  onCollapse,
}: SidebarProps) {
  /**
   * Hover-open sub-navigation — the prototype's `hoverNav`: in the expanded
   * sidebar a section's children reveal on HOVER as well as when the section is
   * active (`showSubs: … && (active || s.hoverNav === r)`), with the same 160ms
   * leave delay it uses, so crossing the gap between item and sub-list doesn't
   * collapse it mid-pointer. Keyboard gets the equivalent via focus-within —
   * Tab onto a section shows its children, further Tabs walk into them.
   * The ACTIVE section stays expanded regardless, exactly as the prototype.
   */
  const [hoverId, setHoverId] = useState<string | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const keepHover = (id: string) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setHoverId(id);
  };
  const releaseHover = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHoverId(null), 160);
  };

  return (
    <div className="dpr-sidebar">
      {onCollapse || header ? (
        <div className="dpr-sidebar__header">
          {onCollapse ? (
            <IconButton
              size="md"
              variant="ghost"
              icon={<Icon name="menu" />}
              label="Collapse navigation"
              aria-expanded
              onClick={onCollapse}
            />
          ) : null}
          {header}
        </div>
      ) : null}

      <nav className="dpr-sidebar__nav" aria-label="Primary">
        <ul className="dpr-sidebar__list">
          {items.map((item) => {
            const isActive = item.id === activeId;
            const badgeText =
              item.badgeCount != null
                ? (item.badgeLabel?.(item.badgeCount) ??
                  `${item.badgeCount} item${item.badgeCount === 1 ? '' : 's'}`)
                : null;
            // Unlike the rail, the label here is already visible text, so the
            // accessible name only needs to *add* the count rather than restate
            // the label. Setting aria-label would override the visible text,
            // which WCAG 2.5.3 (label in name) discourages — so the count goes in
            // as a real, visually-hidden-free suffix inside the row instead: the
            // mono badge is aria-hidden and this span carries the words.
            const Element = item.href ? 'a' : 'button';
            const hasChildren = (item.children?.length ?? 0) > 0;
            const showSub = hasChildren && (isActive || hoverId === item.id);
            return (
              <li
                key={item.id}
                onMouseEnter={hasChildren ? () => keepHover(item.id) : undefined}
                onMouseLeave={hasChildren ? releaseHover : undefined}
                onFocus={hasChildren ? () => keepHover(item.id) : undefined}
                onBlur={hasChildren ? releaseHover : undefined}
              >
                <Element
                  className={['dpr-sidebar__item', isActive ? 'dpr-sidebar__item--active' : null]
                    .filter(Boolean)
                    .join(' ')}
                  href={item.href}
                  type={item.href ? undefined : 'button'}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={item.onSelect}
                >
                  <span className="dpr-sidebar__icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span className="dpr-sidebar__label">{item.label}</span>
                  {item.badgeCount != null ? (
                    <>
                      <Badge variant="neutral" count>
                        {item.badgeCount > 99 ? '99+' : item.badgeCount}
                      </Badge>
                      {/* The words behind the number, for anything reading the row
                          aloud. This is not the badge's text duplicated — the
                          badge shows the digits, this says what they count
                          ("3 need attention"), which is the distinction B-59 was
                          about. */}
                      <VisuallyHidden>{badgeText}</VisuallyHidden>
                    </>
                  ) : null}
                </Element>

                {showSub ? (
                  <ul className="dpr-sidebar__sublist">
                    {item.children!.map((sub) => {
                      const subActive = sub.id === activeSubId;
                      const SubElement = sub.href ? 'a' : 'button';
                      return (
                        <li key={sub.id}>
                          <SubElement
                            className={[
                              'dpr-sidebar__subitem',
                              subActive ? 'dpr-sidebar__subitem--active' : null,
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            href={sub.href}
                            type={sub.href ? undefined : 'button'}
                            aria-current={subActive ? 'page' : undefined}
                            onClick={sub.onSelect}
                          >
                            {sub.label}
                          </SubElement>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </nav>

      {pinnedExtra ? <div className="dpr-sidebar__pinned">{pinnedExtra}</div> : null}
    </div>
  );
}
